import {
  Component,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { StyleClassModule } from 'primeng/styleclass';
import { Sidebar } from 'primeng/sidebar';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { sideBar as sideBarConst } from '../../core/constants/sidebar.const';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgentRoutes, ModuleConst } from '../../core/constants/keys.constant';
import { SidebarService } from '../../services/sidebar.service';
import { AuthService } from '../../services/auth.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LoginResponse } from '../../models/auth.model';
import { CommonModule } from '@angular/common';
import { SideBar } from '../../models/sideBar.model';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DashboardService } from '../../services/dashboard.service';
import { StorageService } from '../../services/StorageService.service';
import { EncryptionService } from '../../services/encryption.service';
import { SidebarHandlingService } from '../../services/sidebarHandling.service';
@Component({
  selector: 'logibids-sidebar',
  standalone: true,
  imports: [
    SidebarModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    StyleClassModule,
    RouterLink,
    TranslateModule,
    CommonModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  isActive = signal(false);
  @ViewChild('sidebarRef') sidebarRef!: Sidebar;
  expandedSubMenus: Set<string> = new Set<string>();

  moduleConst = ModuleConst;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private destroy: Subject<boolean> = new Subject<boolean>();
  sideBarSignal = signal<SideBar[]>(sideBarConst);
  sidebarVisible = model<boolean>(true);
  profile = signal<LoginResponse | null>(null);

  private sidebarService = inject(SidebarService);
  private _authService = inject(AuthService);
  private confirmationService = inject(ConfirmDialogService);
  private translate = inject(TranslateService);
  private dialogRef = inject(DynamicDialogRef);
  private router = inject(Router);
  private _dashboard = inject(DashboardService);
  private storageService = inject(StorageService);
  private _SidebarHandlingService = inject(SidebarHandlingService);
  private _encyrption = inject(EncryptionService);

  private subscriptionSubject$!: Subscription;

  constructor() {
    this.sidebarService.sidebarOpen$.subscribe(isOpen => {
      this.sidebarVisible.set(isOpen);
    });

    this._authService.profileDetails
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profile.set(res);
          this.refreshSidebar(res);
        } else {
          this.getProfile();
        }
      });

    this._dashboard.sidebarChange
      .pipe(takeUntil(this.destroy))
      .subscribe((res: boolean | null) => {
        if (res !== null) {
          this.syncActiveFromCurrentRoute();
        }
      });
  }

  ngOnInit(): void {
    const profile = this._encyrption.decryptLocalStorage('profile');
    if (profile) {
      this.profile.set(profile);
      this.refreshSidebar(profile);
    }
    this.syncActiveFromCurrentRoute();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy)
      )
      .subscribe(() => this.syncActiveFromCurrentRoute());
  }

  getProfile(): void {
    this._authService
      .getProfile()
      .pipe(takeUntil(this.destroy))
      .subscribe((res: LoginResponse | null) => {
        if (res) {
          this.profile.set(res);
          this._authService.profileDetails.next(res);
          this._encyrption.encryptLocalStorage(res, 'profile');
          this.refreshSidebar(res);
        }
      });
  }

  refreshSidebar(profile: LoginResponse): void {
    let sidebar: SideBar[];

    if (
      (profile.type === 'SUB_ADMIN' || profile.type === 'AGENT') &&
      profile.access_role?.permissions
    ) {
      sidebar = this._SidebarHandlingService.filterSidebarData(
        sideBarConst,
        profile.access_role.permissions
      );
    } else {
      sidebar = sideBarConst;
    }

    this.sideBarSignal.set(
      this.filterSidebarByProfileType(
        sidebar,
        profile.type,
        profile.access_role?.permissions
      )
    );
    this.syncActiveFromCurrentRoute();
  }

  /** Agent-only sidebar: promote CRM Dashboard + Chat Listing to top-level items. */
  private filterSidebarByProfileType(
    sidebar: SideBar[],
    profileType: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _permissions?: any[]
  ): SideBar[] {
    const isAgent = profileType === 'AGENT';

    if (!isAgent) {
      return sidebar;
    }

    const result = sidebar
      .map(item => {
        if (item.sidebar_module !== this.moduleConst.CRM_Management) {
          return item;
        }

        const subMenuItems = item.subMenuItems?.filter(
          sub =>
            sub.sidebar_module !== this.moduleConst.CRM_Dashboard &&
            sub.sidebar_module !== this.moduleConst.CRM_ChatListing
        );

        if (!subMenuItems?.length) {
          return null;
        }

        return { ...item, subMenuItems };
      })
      .filter((item): item is SideBar => item !== null);

    const agentTopLevelItems: SideBar[] = [];

    if (
      !result.some(
        item =>
          item.sidebar_module === this.moduleConst.CRM_Dashboard &&
          !item.isSubMenu
      )
    ) {
      agentTopLevelItems.push(this.getAgentDashboardItem());
    }

    if (
      !result.some(
        item =>
          item.sidebar_module === this.moduleConst.CRM_ChatListing &&
          !item.isSubMenu
      )
    ) {
      agentTopLevelItems.push(this.getAgentChatListingItem());
    }

    return agentTopLevelItems.length
      ? [...agentTopLevelItems, ...result]
      : result;
  }

  /** CRM Dashboard as a standalone sidebar item for AGENT users. */
  private getAgentDashboardItem(): SideBar {
    const crmManagement = sideBarConst.find(
      item => item.sidebar_module === this.moduleConst.CRM_Management
    );
    const dashboard = crmManagement?.subMenuItems?.find(
      sub => sub.sidebar_module === this.moduleConst.CRM_Dashboard
    );

    return {
      label: dashboard?.label ?? 'Dashboard',
      path: AgentRoutes.dashboard,
      icon: dashboard?.icon ?? 'assets/images/sidebar/dashboard.svg',
      isSubMenu: false,
      is_add: true,
      is_view: true,
      is_delete: true,
      sidebar_module: this.moduleConst.CRM_Dashboard,
    };
  }

  /** CRM Chat Listing as a standalone sidebar item for AGENT users. */
  private getAgentChatListingItem(): SideBar {
    const crmManagement = sideBarConst.find(
      item => item.sidebar_module === this.moduleConst.CRM_Management
    );
    const chatListing = crmManagement?.subMenuItems?.find(
      sub => sub.sidebar_module === this.moduleConst.CRM_ChatListing
    );
    const chatListingPerm = this._SidebarHandlingService.getReturn(
      this.moduleConst.CRM_ChatListing
    );

    return {
      label: chatListing?.label ?? 'Chat Listing',
      path: AgentRoutes.home,
      icon: chatListing?.icon ?? 'assets/images/sidebar/status_management.svg',
      isSubMenu: false,
      is_add: chatListingPerm?.is_add ?? false,
      is_view: chatListingPerm?.is_view ?? true,
      is_delete: chatListingPerm?.is_delete ?? false,
      sidebar_module: this.moduleConst.CRM_ChatListing,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deSelectItems(item: any) {
    const moduleKey = item.sidebar_module;
    if (this.expandedSubMenus.has(moduleKey)) {
      this.expandedSubMenus.delete(moduleKey);
    } else {
      this.expandedSubMenus.add(moduleKey);
    }
  }

  toggleActive(
    item: SideBar,
    subItems: SideBar | null,
    subSubItem: SideBar | null = null
  ): void {
    this.sideBarSignal.update(current => {
      current.forEach(val => {
        val.active =
          val.sidebar_module === item.sidebar_module && !item.isSubMenu;

        val.subMenuItems?.forEach(list => {
          list.active =
            item.isSubMenu && subItems?.sidebar_module === list.sidebar_module;

          list.subMenuItems?.forEach(res => {
            res.active =
              item.isSubMenu &&
              subSubItem?.sidebar_module === res.sidebar_module;
          });

          list.subModules?.forEach(res => {
            res.active =
              item.isSubMenu &&
              subSubItem?.sidebar_module === res.sidebar_module;
          });
        });
      });
      return [...current];
    });

    const activeState = {
      parentId: item.sidebar_module,
      subItemId: subItems?.sidebar_module || null,
      subSubItemId: subSubItem?.sidebar_module || null,
      isSubMenu: item.isSubMenu || false,
    };

    if (item.isSubMenu) {
      this.expandedSubMenus.add(item.sidebar_module);
      if (subItems) {
        this.expandedSubMenus.add(subItems.sidebar_module);
      }
    }
    this.storageService.updateStorage(JSON.stringify(activeState));
  }

  /** Prefer current URL over stale localStorage (e.g. Agent module `12` vs Dashboard `1`). */
  private syncActiveFromCurrentRoute(): void {
    const url = this.router.url.split('?')[0];
    const hierarchy = this._SidebarHandlingService.getHierarchyByRoute(
      url,
      this.sideBarSignal()
    );

    if (hierarchy) {
      this.applyActiveState(hierarchy);
      this.storageService.updateStorage(JSON.stringify(hierarchy));
      return;
    }

    this.restoreActiveState();
  }

  private applyActiveState(state: {
    parentId: string;
    subItemId: string | null;
    subSubItemId?: string | null;
    isSubMenu: boolean;
  }): void {
    const { parentId, subItemId, subSubItemId, isSubMenu } = state;

    this.sideBarSignal.update(current => {
      current.forEach(val => {
        const isParentMatch = val.sidebar_module === parentId;
        val.active = isParentMatch && !isSubMenu;

        if (isParentMatch && isSubMenu) {
          this.expandedSubMenus.add(val.sidebar_module);
        }

        val.subMenuItems?.forEach(list => {
          const isSubMatch = list.sidebar_module === subItemId;
          list.active = isSubMatch;

          if (isSubMatch && isSubMenu) {
            this.expandedSubMenus.add(list.sidebar_module);
          }

          list.subMenuItems?.forEach(res => {
            res.active = res.sidebar_module === subSubItemId;
            if (res.active && isSubMenu) {
              this.expandedSubMenus.add(list.sidebar_module);
            }
          });

          list.subModules?.forEach(res => {
            res.active = res.sidebar_module === subSubItemId;
            if (res.active && isSubMenu) {
              this.expandedSubMenus.add(list.sidebar_module);
            }
          });
        });
      });
      return [...current];
    });
  }

  restoreActiveState(): void {
    const savedState = localStorage.getItem('activeState');
    if (!savedState) return;

    const state = JSON.parse(savedState);
    // Only restore highlight state; do not navigate away from detail/child routes.
    this.applyActiveState(state);
  }

  onLogoClick(): void {
    const dashboardItem = this.sideBarSignal().find(
      item => item.sidebar_module === this.moduleConst.Dashboard
    );
    if (dashboardItem) {
      this.toggleActive(dashboardItem, null);
    }
  }

  btn_logOut(): void {
    this.confirmationService
      .showConfirmationDialog(' ', this.translate.instant('SureLogout'))
      .then(result => {
        if (result) {
          this._authService.logout().subscribe(() => {
            this.router.navigate(['/auth/login']);
            this.dialogRef.close();
            localStorage.clear();
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
